/** Managed banner references stay portable between the local replica and IC. */
export const BANNER_PREFIX = 'soonscreener-banner:';
export const MAX_BANNER_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_BANNER_BYTES = 384 * 1024;
export const BANNER_WIDTH = 1200;
export const BANNER_HEIGHT = 400;
export type BannerUpload = { content_type: string; data: Uint8Array };
export type BannerSelection = BannerUpload & { previewUrl: string; filename: string };

export function bannerUrl(value: string): string | undefined {
  const match = /^soonscreener-banner:(0|[1-9]\d*)$/.exec(value);
  if (match) {
    const canister = import.meta.env.VITE_BACKEND_CANISTER_ID;
    if (!canister) return undefined;
    if (import.meta.env.VITE_DFX_NETWORK === 'local') {
      const host = new URL(import.meta.env.VITE_DFX_HOST ?? 'http://127.0.0.1:4943');
      return `${host.protocol}//${canister}.raw.localhost${host.port ? `:${host.port}` : ''}/banners/${match[1]}`;
    }
    return `https://${canister}.raw.icp0.io/banners/${match[1]}`;
  }
  try { const url = new URL(value); return url.protocol === 'https:' ? url.href : undefined; } catch { return undefined; }
}

export function bannerMime(bytes: Uint8Array): string | undefined {
  if (bytes.length >= 8 && [137, 80, 78, 71, 13, 10, 26, 10].every((byte, i) => bytes[i] === byte)) return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255) return 'image/jpeg';
  if (bytes.length >= 12 && String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP') return 'image/webp';
  return undefined;
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(Error('This browser could not prepare the image. Try a different PNG, JPEG or WebP.')), type, quality));
}

/** Decode a raster file and submit exactly the center-cropped image shown in review. */
export async function prepareBanner(file: File): Promise<BannerSelection> {
  if (!file.size) throw Error('The image file is empty. Choose a PNG, JPEG or WebP.');
  if (file.size > MAX_BANNER_FILE_BYTES) throw Error('Choose an image smaller than 10 MB.');
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) throw Error('Choose a PNG, JPEG or WebP image. SVG and GIF are not supported.');
  const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  if (bannerMime(header) !== file.type) throw Error('This file does not match its image type. Choose a valid PNG, JPEG or WebP.');
  const sourceUrl = URL.createObjectURL(file);
  const image = new Image();
  try {
    image.src = sourceUrl;
    await image.decode();
    if (!image.naturalWidth || !image.naturalHeight) throw Error('The image has no usable dimensions.');
    if (image.naturalWidth > 8192 || image.naturalHeight > 8192 || image.naturalWidth * image.naturalHeight > 32_000_000) throw Error('This image is too large to process. Resize it to 8192 pixels per side or less.');
    const canvas = document.createElement('canvas'); canvas.width = BANNER_WIDTH; canvas.height = BANNER_HEIGHT;
    const context = canvas.getContext('2d');
    if (!context) throw Error('This browser cannot prepare images. Try another browser.');
    const sourceRatio = image.naturalWidth / image.naturalHeight;
    const cropWidth = sourceRatio > 3 ? image.naturalHeight * 3 : image.naturalWidth;
    const cropHeight = sourceRatio > 3 ? image.naturalHeight : image.naturalWidth / 3;
    context.drawImage(image, (image.naturalWidth - cropWidth) / 2, (image.naturalHeight - cropHeight) / 2, cropWidth, cropHeight, 0, 0, BANNER_WIDTH, BANNER_HEIGHT);
    let blob = await canvasBlob(canvas, 'image/webp', 0.85);
    if (blob.type === 'image/webp') {
      for (const quality of [0.7, 0.5, 0.3]) {
        if (blob.size <= MAX_BANNER_BYTES) break;
        blob = await canvasBlob(canvas, 'image/webp', quality);
      }
    }
    if (!['image/webp', 'image/png'].includes(blob.type)) throw Error('This browser cannot export a supported banner image. Try another browser.');
    if (blob.size > MAX_BANNER_BYTES) throw Error('The prepared image is larger than 384 KB. Try a simpler or smaller image.');
    const data = new Uint8Array(await blob.arrayBuffer());
    return { content_type: blob.type, data, previewUrl: URL.createObjectURL(blob), filename: file.name };
  } catch (reason) {
    if (reason instanceof DOMException) throw Error('The image could not be read. Choose a valid PNG, JPEG or WebP.');
    throw reason;
  } finally { image.src = ''; URL.revokeObjectURL(sourceUrl); }
}

export function restoredBanner(upload: BannerUpload): BannerSelection {
  const data = Uint8Array.from(upload.data);
  return { ...upload, data, filename: 'Saved banner', previewUrl: URL.createObjectURL(new Blob([data], { type: upload.content_type })) };
}
